import { Icon } from "./Icon";
import { useState } from "react";
import { href } from "../lib/route";
import {
  REMINDERS_EMPTY, clockOf, dashboardReminders, reminderBucket, reminderDayWord, whenWords, type Reminder,
} from "../lib/reminders";
import type { ReminderClient, RemindersState, ReminderWrite } from "../routes/useReminders";
import type { ReminderTarget } from "./ReminderWindow";

/* THE REMINDER ROW AND THE DASHBOARD'S CARD (W82 step 6).
 *
 * ONE ROW, drawn by the card and by the Reminders page: a round tick (mark done / reopen), the title, the client it is
 * about, and when. MEANING colours only (amendment (d)): an OVERDUE row's time is in the warn colour; a DONE row is ink,
 * struck through. The tick is a button with `aria-pressed`, never a checkbox in a row (the legal probe's rule). */
export function ReminderRow({ r, now, zone, clients, write, onEdit, timeOnly = false, dayOnly = false }: {
  readonly r: Reminder;
  readonly now: Date;
  readonly zone: string;
  readonly clients: readonly ReminderClient[];
  readonly write: ReminderWrite;
  readonly onEdit: (t: ReminderTarget) => void;
  /** On the page the day is the group's heading, so the row says the time alone. */
  readonly timeOnly?: boolean;
  /** W101 — on the Dashboard a reminder is its text and its DAY (`today` · `tomorrow` · `Fri`), nothing else: no client,
   *  no clock, no `overdue` word (an overdue row's `today` is drawn in the accent instead). */
  readonly dayOnly?: boolean;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const bucket = reminderBucket(r, now, zone);
  const done = bucket === "done";
  const who = dayOnly || r.clientId === null ? null : (clients.find((c) => c.id === r.clientId)?.name ?? null);
  const when = dayOnly ? reminderDayWord(r, now, zone) : timeOnly ? clockOf(r.dueAt, zone) : whenWords(r, now, zone);
  return (
    <li className={`reminder-row is-${bucket}`}>
      <button type="button" className="reminder-tick" aria-pressed={done}
              aria-label={done ? `Reopen: ${r.title}` : `Mark done: ${r.title}`}
              onClick={() => { void write.setDone(r.id, !done).then(setFailed); }}>
        <Icon name="check" stroke={2.4} />
      </button>
      <button type="button" className="reminder-body" onClick={() => onEdit({ mode: "edit", reminder: r })}
              aria-label={`Edit reminder: ${r.title}`}>
        <span className="reminder-title">{r.title}</span>
        {who !== null && <span className="reminder-who">{who}</span>}
      </button>
      <span className="reminder-due">{bucket === "overdue" && !dayOnly ? `${when} · overdue` : when}</span>
      {failed !== null && <p className="reminder-failed" role="alert">{failed}</p>}
    </li>
  );
}

/** THE DASHBOARD'S CARD, the page's last: overdue + today, one line each — the text and its day (W101); `+ Reminder` in the
 *  head; the page is a link. No count in the head: the rows are the count. */
export function RemindersCard({ state, write, now, zone, onEdit }: {
  readonly state: RemindersState;
  readonly write: ReminderWrite;
  readonly now: Date;
  readonly zone: string;
  readonly onEdit: (t: ReminderTarget) => void;
}) {
  const rows = state.status === "ready" ? dashboardReminders(state.rows, now, zone) : [];
  return (
    <section className="queue-card reminders-card" aria-labelledby="reminders-title">
      <header className="queue-card-head">
        <h2 id="reminders-title" className="queue-card-title">Reminders</h2>
        <button type="button" className="ht-button ht-button-quiet reminders-add is-alone"
                onClick={() => onEdit({ mode: "new", clientId: null })}>+ Reminder</button>
      </header>
      {state.status === "loading" && <p className="queue-empty">Loading…</p>}
      {state.status === "error" && <p className="queue-empty" role="alert">Could not load your reminders. {state.message}</p>}
      {state.status === "ready" && (rows.length === 0
        ? <p className="queue-empty">{REMINDERS_EMPTY}</p>
        : (
          <ul className="reminder-rows">
            {rows.map((r) => (
              <ReminderRow key={r.id} r={r} now={now} zone={zone} clients={state.clients} write={write} onEdit={onEdit} dayOnly />
            ))}
          </ul>
        ))}
      <a className="queue-more" href={href({ name: "reminders" })}>All reminders ›</a>
    </section>
  );
}
